import { RunMessagesInline } from '@/components/RunMessagesInline';
import { filterInputs } from '@/components/RunsTable/utils/filterInputs';
import { Text } from '@/design-system/';
import { cn } from '@/utils/tailwind';

import { CodeDataReadOnly, CodeText } from '../../components/Code/Code';
import { DatasetSchema } from '../../types/schema';
import { ForeignDataTreeView } from '../Graph/src/components/debug/common/foreign-data-tree';
import {
  foreignDataToTree,
  parseForeignData,
} from '../Graph/src/data/foreign-data';
import { ExperimentLanguage } from './types';

export function DatasetSessionCompareInputs(props: {
  inputs: unknown;
  dataType: DatasetSchema['data_type'];
  language: ExperimentLanguage;
  truncate: boolean;
  lineClamp?: number;
  heuristicCompactViewDisplayedKey?: string;
}) {
  const filteredInputs = filterInputs(props.inputs, {
    heuristicCompactViewDisplayKey: props.heuristicCompactViewDisplayedKey,
  });
  const truncatedElement = (
    <div
      className={cn(
        'pointer-events-none absolute left-0 right-0 top-0 max-h-[340px] overflow-visible truncate text-ellipsis whitespace-normal break-words break-all text-sm font-medium opacity-0',
        props.truncate &&
          'pointer-events-auto line-clamp-3 max-h-[100px] opacity-100'
      )}
      style={{
        WebkitLineClamp: props.lineClamp,
        transition: 'max-height 0.2s ease, opacity 0.2s ease',
      }}
    >
      {filteredInputs === undefined
        ? ''
        : typeof filteredInputs !== 'string'
        ? JSON.stringify(filteredInputs).slice(0, 500)
        : filteredInputs?.slice(0, 500)}
    </div>
  );

  let inputElement = (
    <div
      style={{ transition: 'max-height 0.2s ease, opacity 0.2s ease' }}
      className={cn(
        'pointer-events-none max-h-[20px] min-h-[20px] whitespace-normal break-all opacity-0',
        !props.truncate && 'pointer-events-auto max-h-[340px] opacity-100'
      )}
    >
      {props.language === 'pretty' ? (
        <ForeignDataTreeView
          data={foreignDataToTree({
            data: parseForeignData(props.inputs),
          })}
        />
      ) : (
        <CodeDataReadOnly
          className={cn(
            'max-h-[20px] opacity-100 transition-all',
            !props.truncate && 'max-h-[340px] opacity-100'
          )}
          language={props.language}
          value={props.inputs}
        />
      )}
    </div>
  );

  if (props.dataType === 'llm') {
    let value = '';
    if (typeof props.inputs === 'string') {
      value = props.inputs;
    } else if (
      props.inputs !== null &&
      typeof props.inputs === 'object' &&
      'input' in props.inputs &&
      typeof props.inputs.input === 'string'
    ) {
      value = props.inputs.input;
    }
    inputElement = (
      <div
        style={{ transition: 'max-height 0.2s ease, opacity 0.2s ease' }}
        className={cn(
          'pointer-events-none max-h-[20px] min-h-[20px] opacity-0',
          !props.truncate && 'pointer-events-auto max-h-[340px] opacity-100'
        )}
      >
        <CodeText
          className={cn(
            'max-h-[100px] transition-all',
            !props.truncate && 'max-h-[340px] opacity-100'
          )}
          value={value}
          readOnly
          removePadding
        />
      </div>
    );
  }

  if (props.dataType === 'chat') {
    // TODO: Clean up, chat datasets are deprecated so don't want to mess with types too much
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let inputMessages: any;
    if (
      props.inputs !== null &&
      typeof props.inputs === 'object' &&
      'input' in props.inputs
    ) {
      inputMessages = props.inputs.input;
    }
    inputElement = inputMessages ? (
      <div
        style={{ transition: 'max-height 0.2s ease, opacity 0.2s ease' }}
        className={cn(
          'pointer-events-none max-h-[20px] min-h-[20px] overflow-y-auto text-ellipsis whitespace-normal break-words break-all opacity-0',
          !props.truncate && 'pointer-events-auto max-h-[340px] opacity-100'
        )}
      >
        <RunMessagesInline messages={inputMessages} />
      </div>
    ) : (
      inputElement
    );
  }

  if (
    !filteredInputs ||
    (typeof filteredInputs === 'object' &&
      Object.keys(filteredInputs).length === 0)
  ) {
    return <Text className="text-disabled">No input</Text>;
  }

  return (
    <div className="relative w-full grow-0">
      {truncatedElement}
      {inputElement}
    </div>
  );
}
